import {
  CallbackSuccessHandler,
  FetchResult,
  ResourceAttributes,
  ResourceType,
  useCreateCallback,
  useUpdateCallback,
} from 'json-api-hooks';
import { FieldValues, UseFormHandleSubmit, UseFormSetError } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import useSnackbar from '../../../hooks/useSnackbar';
import { isString } from '../../../lib/typeCheckers';
import { FormFieldDefinition } from '../ResourceForm';

import parseErrors from './parseErrors';

const useFormSubmit = <T extends ResourceType>(
  type: T,
  id: string | undefined,
  fields: FormFieldDefinition[],
  handleSubmit: UseFormHandleSubmit<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  onSuccess?: CallbackSuccessHandler,
  storageKey?: string,
): [callback: () => void, loading: boolean, clearStorage: () => void] => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [updateCallback, updateLoading] = useUpdateCallback(isString(id) ? { id, type } : undefined);
  const [createCallback, createLoading] = useCreateCallback(type);
  const callback = isString(id) ? updateCallback! : createCallback;
  const loading = isString(id) ? updateLoading : createLoading;

  const handleError = ({ json }: FetchResult) => parseErrors(json, fields, setError);

  const handleSuccess: CallbackSuccessHandler =
    onSuccess ??
    (({ response }) => {
      const location = response.headers.get('Location');

      showSnackbar('De wijzigingen zijn opgeslagen');

      if (location) {
        const url = new URL(location, window.location.origin);
        navigate(url.pathname + url.search + url.hash);
      }
    });
  const clearStorage = () => {
    if (storageKey) {
      Object.keys(sessionStorage).forEach((k) => k.startsWith(storageKey) && sessionStorage.removeItem(k));
    }
  };

  const formCallback = handleSubmit((data) => {
    callback(data as ResourceAttributes<T>)
      .then(handleSuccess)
      .then(clearStorage)
      .catch(handleError);
  });

  return [formCallback, loading ?? false, clearStorage];
};

export default useFormSubmit;
